// src/pages/Documentation.js
import { useState } from "react";
import { FaFilePdf, FaDownload, FaExternalLinkAlt } from "react-icons/fa";
import Layout from "../layout/Layout";
import { PrivateAxios } from "../../request/axios";

// Respecte les doc_type attendus par l'API Flask: "gestion-contrat" / "gestion-utilisateur"
const docs = [
    {
        title: "Gestion des contrats",
        docType: "gestion-contrat",
        fallbackFilename: "gestion-contrat.pdf",
    },
    {
        title: "Gestion des comptes",
        docType: "gestion-compte",
        fallbackFilename: "gestion-compte.pdf",
    },
];

// Extrait un nom de fichier depuis Content-Disposition si dispo
const filenameFromCD = (cd, fallback) => {
    if (!cd) return fallback;
    // gère filename="x.pdf" et RFC 5987 filename*=UTF-8''x.pdf
    const star = /filename\*=(?:UTF-8''|)([^;]+)/i.exec(cd);
    const simple = /filename="?([^";]+)"?/i.exec(cd);
    try {
        const raw = (star?.[1] || simple?.[1] || fallback).replace(/"/g, "");
        return decodeURIComponent(raw);
    } catch {
        return fallback;
    }
};

export default function Documentation() {
    const [downloading, setDownloading] = useState(null); // docType en cours
    const [error, setError] = useState(null);

    const handleDownload = async (doc) => {
        setError(null);
        setDownloading(doc.docType);
        try {
            const response = await PrivateAxios.get(
                `/documentation/${encodeURIComponent(doc.docType)}`,
                {
                    params: { download: true },   // dit au backend de renvoyer attachment
                    responseType: "blob",         // on veut un Blob
                }
            );

            // Récupère le nom du fichier si fourni
            const cd = response.headers?.["content-disposition"];
            const filename = filenameFromCD(cd, doc.fallbackFilename);

            // Crée un lien de téléchargement
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                e?.message ||
                "Erreur lors du téléchargement."
            );
        } finally {
            setDownloading(null);
        }
    };

    return (
        <Layout>
            <div className="min-h-screen p-8 bg-gray-100">
                <h1 className="mb-8 text-3xl font-bold text-center text-black">
                    Documentations
                </h1>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {docs.map((doc) => (
                        <div
                            key={doc.docType}
                            className="p-6 transition bg-white rounded-lg shadow-lg hover:shadow-xl"
                        >
                            <div className="flex items-center">
                                <FaFilePdf className="mr-4 text-4xl text-axa-blue-400" />
                                <div className="flex-1">
                                    <h2 className="text-xl font-semibold text-axa-blue-700">
                                        {doc.title}
                                    </h2>
                                    <p className="mt-1 text-sm text-gray-500">
                                        {doc.fallbackFilename}
                                    </p>
                                </div>
                            </div>

                            <div className="flex justify-end mt-6 space-x-4">
                                <button
                                    type="button"
                                    onClick={() => handleDownload(doc)}
                                    disabled={downloading === doc.docType}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition bg-gray-800 rounded-md hover:bg-black focus:outline-none focus:ring-4 focus:ring-gray-300"
                                    aria-label={`Télécharger ${doc.title}`}
                                    title="Télécharger"
                                >
                                    <FaDownload aria-hidden />
                                    {downloading === doc.docType ? "Téléchargement..." : "Télécharger"}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {error && (
                    <div
                        role="alert"
                        className="p-4 mt-6 text-sm text-red-800 rounded-md bg-red-50"
                    >
                        {error}
                    </div>
                )}
            </div>
        </Layout>
    );
}

